<script setup lang="ts">
const props = defineProps<{
  controller: ReturnType<typeof useModal>;
}>();

const { cartItems, totalPrice, isEmpty } = useCart();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

</script>

<template>
  <LayoutSidebar :controller="props.controller" side="right">
    <aside class="flex-1 overflow-y-auto py-6 px-4 sm:px-6">
      <div class="flex items-start justify-between">
        <h2
          id="slide-over-title"
          class="text-lg font-medium text-secondary-900 py-0"
        >
          Shopping cart
        </h2>
        <div class="ml-3 flex h-7 items-center">
          <button
            type="button"
            class="-m-2 p-2 text-xl bg-transparent text-secondary-400 hover:text-secondary-500"
            data-testid="cart-close-button"
            @click="props.controller.close"
          >
            <span class="sr-only">Close panel</span>
            <div class="w-6 h-6 icon-close-outline" />
          </button>
        </div>
      </div>

      <div class="mt-8">
        <div class="flow-root">
          <ul
            v-if="!isEmpty"
            role="list"
            class="-my-6 px-0 divide-y divide-secondary-200"
          >
            <li
              v-for="cartItem in cartItems"
              :key="cartItem.id"
              class="flex py-6"
            >
              <CheckoutCartItem :cart-item="cartItem" :is-flyout=true />
            </li>
          </ul>
          <div v-else class="flex flex-col items-center justify-center py-20 px-4 h-full">
            <i class="icon-bag-outline text-[60px] md:text-[80px] text-grey-300 mb-4"></i>
            <p class="text-xl text-grey-700 font-normal">{{ $t("cart.emptyCartLabel") }}</p>
            <p class="text-sm text-grey-600 mt-2">{{ $t("cart.emptyCartDescription") }}</p>
          </div>
        </div>
      </div>
    </aside>

    <aside
      :aria-label="$t('cart.sideCartAriaLabel')"
      class="border-t border-secondary-200 py-6 px-4 sm:px-6"
    >
      <div
        class="flex justify-between text-base font-medium text-secondary-900"
      >
        <p>{{ $t("cart.subtotal") }}</p>
        <SharedPrice
          :value="totalPrice"
          data-testid="cart-subtotal" 
          class="text-red text-md md:text-lg font-bold whitespace-nowrap"
        />
      </div>
      <p class="mt-2 text-sm text-secondary-500">
        {{ $t("cart.shippingAndTaxes") }}
      </p>
      <div class="mt-2">
        <NuxtLink
          class="btn btn-primary btn-full"
          :class="{
            '!text-[#6E7381] bg-[#f9f9f9] cursor-default': isEmpty,
            'text-white bg-primary hover:bg-dark': !isEmpty,
          }"
          :to="formatLink(isEmpty ? '' : '/checkout')"
          data-testid="cart-checkout-link"
        >
          {{ $t("cart.checkout") }}
        </NuxtLink>

        <NuxtLink
          class="flex items-center justify-center py-3 text-sm font-medium text-dark"
          :to="formatLink(`/checkout/cart`)"
          data-testid="cart-checkout-shopping-cart"
          @click="props.controller.close"
        >
          {{ $t("cart.goToShoppingCart") }}
        </NuxtLink>
      </div>
      <div class="mt-2 flex justify-center text-center text-sm text-dark">
        <p>
          or
          <button
            type="button"
            class="font-medium bg-transparent"
            data-testid="cart-continue-button"
            @click="props.controller.close"
          >
            {{ $t("cart.continueShopping") }}<span aria-hidden="true"> &rarr;</span>
          </button>
        </p>
      </div>
    </aside>
  </LayoutSidebar>
</template>
